import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, Trash2, Wand2, Upload, Sparkles, Users, X, LayoutGrid, Lock, ChevronDown } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { loadEntitlements } from '../lib/entitlements';
import { downscaleToDataUrl } from '../lib/img';

const VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
const inputCls = 'w-full rounded-md border border-[#2A2A30] bg-[#141417] px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none';

function Editor({ initial, styles, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || '');
  const [persona, setPersona] = useState(initial?.persona || '');
  const [voice, setVoice] = useState(initial?.voice || 'nova');
  const [language, setLanguage] = useState(initial?.language || 'English');
  const [styleSlug, setStyleSlug] = useState(initial?.style_slug || (styles[0]?.slug || ''));
  const [accent, setAccent] = useState(initial?.accent_color || '#22D3EE');
  const [aspect, setAspect] = useState(initial?.aspect_default || '9:16');
  const [refs, setRefs] = useState(initial?.references || []); // [{id,url}]
  const [cover, setCover] = useState(initial?.cover_asset_id || (initial?.references?.[0]?.id || null));
  const [prompt, setPrompt] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  // Video settings live behind a disclosure: collapsed for a new person, open
  // when editing one that already carries them, so nothing filled disappears.
  const [videoOpen, setVideoOpen] = useState(
    !!initial?.id && !!(initial?.voice || initial?.language || initial?.style_slug || initial?.aspect_default || initial?.accent_color)
  );

  const addRef = (asset, makeCover) => {
    setRefs((p) => [...p, asset]);
    if (makeCover || !cover) setCover(asset.id);
  };

  const generateFace = async () => {
    setGenBusy(true);
    try {
      const r = await api.generateInfluencerFace({ prompt });
      addRef(r.asset, refs.length === 0);
      toast({ title: 'Face generated' });
    } catch (e) { toast({ title: 'Generation failed', description: e.message }); }
    setGenBusy(false);
  };

  const uploadFace = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    (async () => {
      setGenBusy(true);
      try {
        const dataUrl = await downscaleToDataUrl(f, 1280);
        const a = await api.createAsset({ type: 'influencer', name: f.name, dataUrl });
        addRef({ id: a.id, url: a.url }, refs.length === 0);
        toast({ title: 'Face uploaded' });
      } catch (err) { toast({ title: 'Upload failed', description: err.message }); }
      setGenBusy(false);
    })();
  };

  const removeRef = (id) => {
    setRefs((p) => p.filter((r) => r.id !== id));
    if (cover === id) setCover((refs.find((r) => r.id !== id) || {}).id || null);
  };

  const save = async () => {
    if (!name.trim()) { toast({ title: 'Give this person a name' }); return; }
    setSaving(true);
    const payload = {
      name: name.trim(), persona, voice, language, style_slug: styleSlug,
      accent_color: accent, aspect_default: aspect,
      reference_asset_ids: refs.map((r) => r.id), cover_asset_id: cover,
    };
    try {
      const saved = initial?.id ? await api.updateInfluencer(initial.id, payload) : await api.createInfluencer(payload);
      onSaved(saved);
    } catch (e) {
      if (e.status === 402 || /limit/i.test(e.message || '')) {
        toast({ title: 'People limit reached', description: 'Unlock unlimited people with Pro Unlimited (OTO1).' });
      } else { toast({ title: 'Save failed', description: e.message }); }
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-[#2A2A30] bg-[#141417]" onClick={(e) => e.stopPropagation()} data-testid="influencer-editor">
        <div className="flex items-center justify-between border-b border-[#2A2A30] px-5 py-3.5">
          <h3 className="font-serif-display text-lg text-white">{initial?.id ? 'Edit person' : 'New person'}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid gap-5 p-5 md:grid-cols-2">
          {/* Face */}
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Identity / face</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {refs.map((r) => (
                <div key={r.id} className={`relative aspect-square overflow-hidden rounded-lg border ${cover === r.id ? 'border-node-generator' : 'border-[#2A2A30]'}`}>
                  <img src={fileUrl(r.url)} alt="" className="h-full w-full cursor-pointer object-cover" onClick={() => setCover(r.id)} />
                  <button onClick={() => removeRef(r.id)} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white/80 hover:text-red-400"><Trash2 size={11} /></button>
                  {cover === r.id && <span className="absolute bottom-1 left-1 rounded bg-node-generator px-1 text-[8px] font-bold text-black">COVER</span>}
                </div>
              ))}
              {refs.length === 0 && <div className="col-span-3 flex aspect-[3/1] items-center justify-center rounded-lg border border-dashed border-[#2A2A30] text-xs text-white/30">Generate or upload a face</div>}
            </div>
            <div className="mt-3 space-y-2">
              <textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe the person: e.g. 25yo Latina lifestyle creator, warm, casual" data-testid="influencer-face-prompt" className={`${inputCls} resize-none`} />
              <div className="flex gap-2">
                <button onClick={generateFace} disabled={genBusy} data-testid="influencer-generate-face" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-node-generator/50 bg-node-generator/10 py-2 text-sm font-medium text-node-generator hover:bg-node-generator/20 disabled:opacity-50">
                  {genBusy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Generate
                </button>
                <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-[#2A2A30] bg-[#0E0E10] px-3 py-2 text-sm text-white/80 hover:border-white/30">
                  <Upload size={14} /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={uploadFace} data-testid="influencer-upload-face" />
                </label>
              </div>
              <p className="text-[10px] text-white/40">Tip: after creating, use “Add angles” to build a consistency sheet so the face stays identical across generations.</p>
            </div>
          </div>

          {/* Meta */}
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="influencer-name" className={`${inputCls} mt-1`} placeholder="e.g. Lucía" />
            </div>
            <div>
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Persona / tone</label>
              <textarea rows={2} value={persona} onChange={(e) => setPersona(e.target.value)} className={`${inputCls} mt-1 resize-none`} placeholder="warm, upbeat lifestyle creator" />
            </div>
            <div className="rounded-lg border border-[#2A2A30]">
              <button
                type="button"
                onClick={() => setVideoOpen((v) => !v)}
                data-testid="influencer-video-settings-toggle"
                className="flex w-full items-center justify-between px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/50 hover:text-white/80"
              >
                Video settings (optional)
                <ChevronDown size={14} className={`shrink-0 transition-transform ${videoOpen ? 'rotate-180' : ''}`} />
              </button>
              {videoOpen && (
                <div className="space-y-3 px-3 pb-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Voice</label>
                      <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="influencer-voice" className={`${inputCls} mt-1`}>{VOICES.map((v) => <option key={v} value={v}>{v}</option>)}</select>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Script language</label>
                      <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="influencer-language" className={`${inputCls} mt-1`}><option>English</option><option>Spanish</option></select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Default style</label>
                      <select value={styleSlug} onChange={(e) => setStyleSlug(e.target.value)} className={`${inputCls} mt-1`}>{styles.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}</select>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Default format</label>
                      <select value={aspect} onChange={(e) => setAspect(e.target.value)} className={`${inputCls} mt-1`}><option>9:16</option><option>1:1</option><option>16:9</option></select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium uppercase tracking-wider text-white/50">Accent color</label>
                    <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="mt-1 h-9 w-full cursor-pointer rounded-md border border-[#2A2A30] bg-[#141417]" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[#2A2A30] px-5 py-3.5">
          <button onClick={onClose} className="rounded-full border border-[#2A2A30] px-4 py-2 text-sm text-white/70 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="influencer-save" className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black disabled:opacity-50">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {initial?.id ? 'Save' : 'Create person'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Influencers() {
  const [items, setItems] = useState([]);
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // influencer object or {} for new
  const [ent, setEnt] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Face assets (type influencer/model) that no person references. They used
  // to be invisible — uploaded through an editor that was later cancelled, or
  // left behind by a deleted person — and therefore undeletable. Shown
  // collapsed and only when there is something to show.
  const [orphans, setOrphans] = useState([]);
  const [orphansOpen, setOrphansOpen] = useState(false);
  const [orphanBusy, setOrphanBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, st, e, allAssets] = await Promise.all([
        api.listInfluencers(), api.getStyleTemplates(), loadEntitlements(true),
        api.listAssets().catch(() => []),
      ]);
      setItems(list); setStyles(st || []); setEnt(e);
      // Client-side join: a face asset is orphaned when no person's
      // reference list (nor cover) claims it.
      const claimed = new Set();
      (list || []).forEach((p) => {
        (p.reference_asset_ids || []).forEach((id) => claimed.add(id));
        (p.references || []).forEach((r) => claimed.add(r.id));
        if (p.cover_asset_id) claimed.add(p.cover_asset_id);
      });
      setOrphans((allAssets || []).filter(
        (a) => (a.type === 'influencer' || a.type === 'model') && !claimed.has(a.id)));
    } catch (err) {
      toast({ title: 'Could not load your people', description: 'Check your connection and reload.' });
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeOrphan = async (a) => {
    const ok = window.confirm(`Delete the photo "${a.name || 'Untitled'}"?\n\nNo person is using it. This cannot be undone.`);
    if (!ok) return;
    setOrphanBusy(a.id);
    try {
      await api.deleteAsset(a.id);
      setOrphans((p) => p.filter((x) => x.id !== a.id));
      toast({ title: 'Photo deleted', description: a.name || undefined });
    } catch (e) {
      toast({ title: 'Could not delete it', description: e.message });
    }
    setOrphanBusy(null);
  };

  const limit = ent?.limits?.influencers; // null = unlimited
  const atLimit = limit != null && items.length >= limit;

  const onSaved = (saved) => {
    setEditing(null);
    setItems((p) => {
      const idx = p.findIndex((x) => x.id === saved.id);
      if (idx >= 0) { const n = [...p]; n[idx] = saved; return n; }
      return [saved, ...p];
    });
    toast({ title: 'Person saved' });
  };

  const remove = async (id) => {
    try { await api.deleteInfluencer(id); setItems((p) => p.filter((x) => x.id !== id)); }
    catch (e) { toast({ title: 'Delete failed', description: e.message }); }
  };

  const addAngles = async (id) => {
    setBusyId(id);
    const before = (items.find((x) => x.id === id)?.references || []).length;
    try {
      await api.influencerConsistency(id);
      let done = false;
      let aborted = false;
      let failures = 0;
      for (let i = 0; i < 30 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        let upd;
        try { upd = await api.getInfluencer(id); failures = 0; }
        catch {
          failures++;
          if (failures >= 5) {
            aborted = true;
            toast({ title: 'Lost contact with the server', description: 'Could not check progress after several attempts. Refresh the page in a moment.' });
            break;
          }
          continue;
        }
        if ((upd.references || []).length > before) {
          setItems((p) => p.map((x) => (x.id === id ? upd : x)));
          toast({ title: 'Consistency angles added', description: 'These references keep the face identical across generations.' });
          done = true;
        } else if (upd.consistency_status === 'ready') {
          setItems((p) => p.map((x) => (x.id === id ? upd : x)));
          toast({ title: 'Done', description: 'Consistency pass finished.' });
          done = true;
        }
      }
      if (!done && !aborted) toast({ title: 'Still processing', description: 'Angles are taking longer than usual. Refresh in a moment.' });
    } catch (e) { toast({ title: 'Could not add angles', description: e.message }); }
    setBusyId(null);
  };

  return (
    <div className="min-h-screen bg-[#0E0E10] text-white">
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-serif-display text-3xl">Your people</h1>
            <p className="mt-1 text-sm text-white/50">Reusable people for your shots. Identity, voice and style stay consistent across every photograph, UGC video and ad.</p>
          </div>
          {atLimit ? (
            <button onClick={() => toast({ title: 'People limit reached', description: 'Unlock unlimited people with Pro Unlimited (OTO1).' })} data-testid="influencer-locked" className="inline-flex h-10 items-center gap-1.5 rounded-full border border-node-model/50 bg-node-model/10 px-4 text-sm font-medium text-node-model">
              <Lock size={15} /> Limit reached · Unlock more
            </button>
          ) : (
            <button onClick={() => setEditing({})} data-testid="new-influencer-btn" className="inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-medium text-black hover:bg-white/90">
              <Plus size={15} /> New person
            </button>
          )}
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
        ) : items.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-[#2A2A30] p-12 text-center">
            <Users size={26} className="mx-auto text-white/30" />
            <p className="mt-3 text-white/60">Create your first person to appear in your shots and UGC videos.</p>
            <button onClick={() => setEditing({})} data-testid="new-influencer-empty" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black"><Plus size={15} /> New person</button>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="influencer-grid">
            {items.map((inf) => (
              <div key={inf.id} className="overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#141417]" data-testid={`influencer-card-${inf.id}`}>
                <div className="relative aspect-[4/3] bg-[#0E0E10]">
                  {inf.cover_url ? <img src={fileUrl(inf.cover_url)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Users size={26} className="text-white/20" /></div>}
                  <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white/70">{(inf.references || []).length} refs</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: inf.accent_color || '#22D3EE' }} />
                    <p className="truncate font-medium">{inf.name}</p>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs text-white/45">{inf.persona || 'No persona set'}</p>
                  <p className="mt-2 text-[11px] text-white/40">Voice: {inf.voice} · {inf.language}</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => setEditing(inf)} className="flex-1 rounded-md border border-[#2A2A30] py-1.5 text-xs text-white/80 hover:border-white/30">Edit</button>
                    <button onClick={() => addAngles(inf.id)} disabled={busyId === inf.id} data-testid={`influencer-angles-${inf.id}`} className="inline-flex items-center gap-1 rounded-md border border-node-generator/40 bg-node-generator/10 px-2.5 py-1.5 text-xs text-node-generator disabled:opacity-50">
                      {busyId === inf.id ? <Loader2 size={12} className="animate-spin" /> : <LayoutGrid size={12} />} Angles
                    </button>
                    <button onClick={() => remove(inf.id)} className="rounded-md border border-[#2A2A30] px-2.5 py-1.5 text-white/50 hover:border-red-500/50 hover:text-red-400"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Orphaned face photos: real files taking storage that no page
            listed. Collapsed because on a healthy account it is housekeeping,
            not the point of the page. */}
        {!loading && orphans.length > 0 && (
          <div className="mt-10 rounded-2xl border border-[#2A2A30]">
            <button
              type="button"
              onClick={() => setOrphansOpen((v) => !v)}
              data-testid="orphan-faces-toggle"
              className="flex w-full items-center justify-between px-5 py-3.5 text-left text-sm text-white/60 hover:text-white"
            >
              <span>Unattached face photos ({orphans.length})</span>
              <ChevronDown size={15} className={`shrink-0 transition-transform ${orphansOpen ? 'rotate-180' : ''}`} />
            </button>
            {orphansOpen && (
              <div className="border-t border-[#2A2A30] p-5">
                <p className="text-xs text-white/40">
                  Face photos that no person uses — usually left over from a cancelled editor or a deleted person. They still take storage; delete what you do not need.
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  {orphans.map((a) => (
                    <div key={a.id} className="group relative aspect-square overflow-hidden rounded-lg border border-[#2A2A30] bg-[#0E0E10]">
                      {a.url && <img src={fileUrl(a.url)} alt={a.name || ''} className="h-full w-full object-cover" />}
                      <button
                        onClick={() => removeOrphan(a)}
                        disabled={orphanBusy === a.id}
                        data-testid={`orphan-delete-${a.id}`}
                        title={`Delete ${a.name || 'this photo'}`}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white/70 hover:text-red-400 disabled:opacity-50"
                      >
                        {orphanBusy === a.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {editing && <Editor initial={editing.id ? editing : null} styles={styles} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}
